import type {
  BudgetClientRef,
  BudgetDocument,
  BudgetLine,
  BudgetLineOrigin,
  BudgetLineType,
  BudgetTotals,
} from '../types'

export function emptyTotals(): BudgetTotals {
  return {
    quantity: 0,
    costTotal: 0,
    subtotal: 0,
    discountTotal: 0,
    shipping: 0,
    taxes: 0,
    total: 0,
    profit: 0,
    marginPercent: 0,
  }
}

export function defaultClient(partial?: Partial<BudgetClientRef>): BudgetClientRef {
  return {
    id: partial?.id ?? null,
    name: partial?.name ?? '',
    document: partial?.document,
    email: partial?.email,
    phone: partial?.phone,
  }
}

export function defaultOrigin(
  partial?: Partial<BudgetLineOrigin>,
): BudgetLineOrigin {
  return {
    source: partial?.source ?? 'manual',
    productId: partial?.productId,
    fingerprint: partial?.fingerprint,
    supplierId: partial?.supplierId,
    supplierName: partial?.supplierName,
    externalRef: partial?.externalRef,
  }
}

export function createEmptyDocument(input: {
  id: string
  number: string
  title: string
  client?: Partial<BudgetClientRef>
  nowIso: string
}): BudgetDocument {
  return {
    id: input.id,
    number: input.number,
    title: input.title,
    status: 'DRAFT',
    client: defaultClient(input.client),
    lines: [],
    totals: emptyTotals(),
    globalDiscountPercent: 0,
    globalDiscountValue: 0,
    shipping: 0,
    taxRatePercent: 0,
    notes: '',
    version: 1,
    createdAt: input.nowIso,
    updatedAt: input.nowIso,
  }
}

export function normalizeLine(
  partial: Omit<BudgetLine, 'id' | 'position'> & { id: string; position: number },
): BudgetLine {
  const quantity = Math.max(0, partial.quantity)
  const unitCost = Math.max(0, partial.unitCost)
  const unitPrice = Math.max(0, partial.unitPrice)
  const discountPercent = clamp(partial.discountPercent, 0, 100)
  const discountValue = Math.max(0, partial.discountValue)

  return {
    id: partial.id,
    type: partial.type,
    description: partial.description.trim(),
    quantity,
    unitCost,
    unitPrice,
    discountPercent,
    discountValue,
    position: partial.position,
    origin: defaultOrigin(partial.origin),
    meta: partial.meta,
  }
}

export function isProductLike(type: BudgetLineType): boolean {
  return type === 'product' || type === 'service' || type === 'labor' || type === 'other'
}

function clamp(value: number, min: number, max: number): number {
  if (Number.isNaN(value)) return min
  return Math.min(max, Math.max(min, value))
}
